/**
 * CUSTOMER-R4.15-E — deterministic product recommendations (no ML).
 * Placement-ready interface for later Analytics/CRO/Retention consumers.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import {
  CROSS_SELL_CATEGORY_RULES,
  CROSS_SELL_GLOBAL_FALLBACK,
  rankRecommendationsForCart,
  recommendForProduct,
  type CrossSellCandidate,
} from "./cross-sell-rules.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { resolveProductTitle } from "./product-title-resolver.js";

export type RecommendationPlacement =
  | "HOME_FOR_YOU"
  | "PDP_CROSS_SELL"
  | "PDP_RECOMMENDED"
  | "CART_CROSS_SELL"
  | "PRECHECKOUT_CROSS_SELL"
  | "CHECKOUT_CROSS_SELL"
  | "POST_PURCHASE"
  | "SEARCH_CONTINUATION";

export type RecommendationStrategy =
  | "cross_sell_rules"
  | "brand_country_complement"
  | "recently_viewed"
  | "global_fallback";

export type ProductRecommendationItem = CrossSellCandidate & {
  placement: RecommendationPlacement;
  strategy: RecommendationStrategy;
  rank: number;
};

export type RecommendationRequest = {
  placement: RecommendationPlacement;
  catalog: CatalogV2Product[];
  lang?: StoreV2Lang;
  anchorProductIds?: string[];
  cartProductIds: string[];
  customerContext?: { segment?: string } | null;
  excludedProductIds?: ReadonlySet<string> | string[];
  limit?: number;
  rotationCursor?: number;
};

export type RecommendationResult = {
  items: ProductRecommendationItem[];
  strategy: RecommendationStrategy;
  rotationToken: number;
  exhausted: boolean;
  poolSize: number;
  rotationIndex: number;
};

/**
 * Deterministic next-3 window. Does not wrap inside a window.
 * After the ordered pool is exhausted, nextCursor returns to 0 (new cycle).
 */
export function sliceRecommendationWindow<T>(
  pool: T[],
  cursor: number,
  limit: number,
): { items: T[]; nextCursor: number; exhausted: boolean; rotationIndex: number } {
  const pageSize = Math.min(Math.max(limit, 1), 3);
  if (pool.length === 0) {
    return { items: [], nextCursor: 0, exhausted: true, rotationIndex: 0 };
  }
  const start = Math.max(0, Math.trunc(cursor));
  const safeStart = start >= pool.length ? 0 : start;
  const items: T[] = [];
  for (let i = 0; i < pageSize && safeStart + i < pool.length; i += 1) {
    items.push(pool[safeStart + i]!);
  }
  const consumedTo = safeStart + items.length;
  const exhausted = consumedTo >= pool.length;
  const nextCursor = exhausted ? 0 : consumedTo;
  return {
    items,
    nextCursor,
    exhausted,
    rotationIndex: Math.floor(safeStart / pageSize),
  };
}

export function getCartJourneyRecommendations(input: RecommendationRequest): RecommendationResult {
  const lang = input.lang ?? "ua";
  const limit = Math.min(Math.max(input.limit ?? 3, 1), 3);
  const cartIds = input.cartProductIds.filter(Boolean);
  const exclude = toExcludeSet(input.excludedProductIds);
  for (const id of cartIds) exclude.add(id);
  for (const id of input.anchorProductIds ?? []) exclude.add(id);

  const ranked = rankRecommendationsForCart(input.catalog, cartIds, lang, 12, {
    customerSegment: input.customerContext?.segment ?? "anon",
  }).filter((row) => isEligible(row.product, exclude));

  const window = sliceRecommendationWindow(ranked, input.rotationCursor ?? 0, limit);
  const placement: RecommendationPlacement =
    input.placement === "CHECKOUT_CROSS_SELL" || input.placement === "PRECHECKOUT_CROSS_SELL"
      ? input.placement
      : "CART_CROSS_SELL";
  const items = window.items.map((row, idx) => ({
    ...row,
    placement,
    strategy: "cross_sell_rules" as const,
    rank: idx + 1,
  }));
  return {
    items,
    strategy: "cross_sell_rules",
    rotationToken: window.nextCursor,
    exhausted: window.exhausted,
    poolSize: ranked.length,
    rotationIndex: window.rotationIndex,
  };
}

export type GetProductRecommendationsInput = {
  productId: string;
  product: CatalogV2Product;
  catalog: CatalogV2Product[];
  lang?: StoreV2Lang;
  placement: RecommendationPlacement;
  max?: number;
  excludeIds?: ReadonlySet<string> | string[];
  customerContext?: { segment?: string } | null;
  cartContext?: { productIds?: string[] } | null;
};

function toExcludeSet(excludeIds?: ReadonlySet<string> | string[]): Set<string> {
  if (!excludeIds) return new Set();
  if (Array.isArray(excludeIds)) return new Set(excludeIds.map(String));
  return new Set([...excludeIds].map(String));
}

function titleOfProduct(p: CatalogV2Product, lang: StoreV2Lang): string {
  return resolveProductTitle(p, lang).title;
}

function isEligible(p: CatalogV2Product, exclude: Set<string>): boolean {
  if (exclude.has(p.id)) return false;
  if (p.isSellable === false) return false;
  if (p.stockStatus === "out_of_stock" || p.stockStatus === "deleted") return false;
  return true;
}

function relatedCategoryTargets(sourceKeys: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const src of sourceKeys) {
    for (const t of CROSS_SELL_CATEGORY_RULES[src] ?? []) {
      if (!seen.has(t)) {
        seen.add(t);
        out.push(t);
      }
    }
  }
  for (const t of CROSS_SELL_GLOBAL_FALLBACK) {
    if (!seen.has(t) && !sourceKeys.includes(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

/** Complementary / “may like” — brand → country → related categories; never current SKU. */
function recommendComplementary(
  catalog: CatalogV2Product[],
  product: CatalogV2Product,
  lang: StoreV2Lang,
  max: number,
  exclude: Set<string>,
): ProductRecommendationItem[] {
  const brand = String(product.brand ?? "").trim().toLowerCase();
  const country = String(product.country ?? "").trim().toLowerCase();
  const targets = new Set(relatedCategoryTargets(product.categoryKeys));
  const pool = catalog.filter((p) => isEligible(p, exclude));

  const scored = pool
    .map((p) => {
      let score = 0;
      const pb = String(p.brand ?? "").trim().toLowerCase();
      const pc = String(p.country ?? "").trim().toLowerCase();
      if (brand && pb && pb === brand) score += 40;
      if (country && pc && pc === country) score += 20;
      if (p.categoryKeys.some((k) => targets.has(k))) score += 30;
      // Soft penalty for same primary category (avoid “3 more from Tea only”).
      if (product.categoryKeys[0] && p.categoryKeys[0] === product.categoryKeys[0]) score -= 8;
      return { p, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || String(a.p.id).localeCompare(String(b.p.id)));

  return scored.slice(0, max).map((row, idx) => ({
    product: row.p,
    title: titleOfProduct(row.p, lang),
    reasonCategory: row.p.categoryKeys[0] ?? "global",
    placement: "PDP_RECOMMENDED" as const,
    strategy: "brand_country_complement" as const,
    rank: idx + 1,
  }));
}

/**
 * Canonical recommendation entrypoint (deterministic R4.15-E).
 * Later: Analytics/CRO/Retention can swap strategy without changing placements.
 */
export function getProductRecommendations(
  input: GetProductRecommendationsInput,
): ProductRecommendationItem[] {
  const lang = input.lang ?? "ua";
  const max = Math.min(Math.max(input.max ?? 3, 1), 8);
  const exclude = toExcludeSet(input.excludeIds);
  exclude.add(input.productId);
  exclude.add(input.product.id);

  if (
    input.placement === "PDP_CROSS_SELL" ||
    input.placement === "CART_CROSS_SELL" ||
    input.placement === "PRECHECKOUT_CROSS_SELL" ||
    input.placement === "CHECKOUT_CROSS_SELL"
  ) {
    const base = recommendForProduct(input.catalog, input.product, lang, max, {
      customerSegment: input.customerContext?.segment ?? "anon",
    }).filter((c) => isEligible(c.product, exclude));
    return base.slice(0, max).map((c, idx) => ({
      ...c,
      placement: input.placement,
      strategy: "cross_sell_rules" as const,
      rank: idx + 1,
    }));
  }

  if (input.placement === "PDP_RECOMMENDED" || input.placement === "POST_PURCHASE") {
    return recommendComplementary(input.catalog, input.product, lang, max, exclude);
  }

  return [];
}

/** Category ids to fetch as lightweight merch pools (no full catalog). */
export function merchPoolCategoryIdsForProduct(product: CatalogV2Product, limit = 3): string[] {
  const targets = relatedCategoryTargets(product.categoryKeys);
  const out: string[] = [];
  for (const id of targets) {
    if (out.length >= limit) break;
    out.push(id);
  }
  // Always include one page from own category as soft fill (still exclude current SKU later).
  const own = product.categoryKeys[0];
  if (own && !out.includes(own) && out.length < limit + 1) out.push(own);
  return out.slice(0, Math.max(limit, 1));
}
